import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const MENU_MIN_WIDTH = 180;
const MENU_OFFSET = 8;
const VIEWPORT_PADDING = 8;

export default function ActionMenu({
  items,
  ariaLabel = "Actions",
  trigger,
  align = "right",
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const menuWidth = Math.max(
      menuRef.current?.offsetWidth ?? 0,
      MENU_MIN_WIDTH,
    );

    const fitsBelow =
      rect.bottom + MENU_OFFSET + menuHeight <=
      window.innerHeight - VIEWPORT_PADDING;
    const top = fitsBelow
      ? rect.bottom + MENU_OFFSET
      : Math.max(VIEWPORT_PADDING, rect.top - MENU_OFFSET - menuHeight);

    let left = align === "right" ? rect.right - menuWidth : rect.left;
    left = Math.min(
      Math.max(left, VIEWPORT_PADDING),
      window.innerWidth - menuWidth - VIEWPORT_PADDING,
    );

    setPosition({ top, left });
  }, [align]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, { capture: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("resize", handleScroll);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  const toggle = () => setOpen((prev) => !prev);

  const defaultTrigger = (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-haspopup="true"
      aria-expanded={open}
      onClick={toggle}
      className="inline-flex h-8 w-8 items-center justify-center border-0 bg-transparent text-(--text-muted) hover:text-(--text-primary) focus:outline-none"
    >
      <span aria-hidden="true" className="text-lg leading-none">
        ⋮
      </span>
    </button>
  );

  return (
    <div ref={triggerRef} className="relative inline-block text-left">
      {trigger ? trigger({ open, toggle, ariaLabel }) : defaultTrigger}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? "visible" : "hidden",
            }}
            className="fixed z-[1300] min-w-[180px] overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) shadow-[var(--shadow-popover)]"
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    item.onClick?.();
                  }}
                  className={`flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-2 text-left text-sm transition-colors ${
                    item.danger
                      ? "text-(--status-danger) hover:text-(--status-danger)/80"
                      : "text-(--text-primary) hover:text-(--brand-primary)"
                  }`}
                >
                  {Icon &&
                    (typeof Icon === "string" ? (
                      <span
                        aria-hidden="true"
                        className="text-base leading-none"
                      >
                        {Icon}
                      </span>
                    ) : (
                      <Icon size={16} strokeWidth={2} />
                    ))}
                  {item.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
